/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import BatchSearchSubaccountListModal from "./BatchSearchSubaccountListModal";
import ProviderListModal from "../../shared/ProviderListModal";

const InputStyle =
  "daterange-picker font-medium text-navy-700 h-10 mt-2 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)",
      borderRadius: "2px",
      _hover: {
        background: "gray",
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray",
      color: "white",
    },
    selectedBtnProps: {
      background: "black",
      color: "white",
    },
    todayBtnProps: {},
  },
  inputProps: {
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9",
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

interface ProviderAccount {
  label: string;
  value: string;
}

interface Provider {
  id: string;
  name: string;
  provider_name: string;
  provider_account: ProviderAccount[];
  method: any[];
}

interface ProviderMethod {
  label: string;
  value: string;
}

const BatchSearch: React.FC<{
  showFilters: boolean;
  isLoading: boolean;
  setIsFilterLoading: any;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  setSelectedDates: any;
  selectedDates: any;
  providerList: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
  assetList: any;
}> = ({
  isLoading,
  showFilters,
  setIsFilterLoading,
  isFilterLoading,
  filters,
  setFilters,
  setSelectedDates,
  selectedDates,
  providerList,
  handleSearchSubmit,
  handleSearchClear,
  assetList,
}) => {
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    const [maxDate, setMaxDate] = useState<any>(new Date());

    // Modal state for subaccount selection
    const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
    const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);

    // Modal state for provider selection
    const [isProviderModalOpen, setIsProviderModalOpen] = useState<boolean>(false);
    const [selectedProvider, setSelectedProvider] = useState<any>(null);

    // Method list state
    const [methodList, setMethodList] = useState<any>([]);

    // Handler for subaccount selection from modal
    const handleSubaccountSelect = (subaccountData: any) => {
      setSelectedSubaccount(subaccountData);
      localStorage.setItem("subaccountIdToFilterBatch", subaccountData.id);
      setFilters({ ...filters, subaccount_id: subaccountData.id });
      setIsSubaccountModalOpen(false);
    };

    // Clear subaccount selection
    const handleClearSubaccount = () => {
      setSelectedSubaccount(null);
      localStorage.setItem("subaccountIdToFilterBatch", "");
      setFilters({ ...filters, subaccount_id: "" });
    };

    // Handler for provider selection from modal
    const handleProviderSelect = (providerData: any) => {
      setSelectedProvider(providerData);
      setFilters({ ...filters, provider_id: providerData.id, payout_method: "" });

      // Build method list from selected provider
      const methods = providerData.method?.map((method: any) => ({
        label: method.method_name,
        value: method.id,
      })) || [];
      setMethodList(methods);

      setIsProviderModalOpen(false);
    };

    // Clear provider selection
    const handleClearProvider = () => {
      setSelectedProvider(null);
      setFilters({ ...filters, provider_id: "", payout_method: "" });
      setMethodList([]);
    };

    const handleClear = () => {
      setSelectedSubaccount(null);
      setSelectedProvider(null);
      setMethodList([]);
      localStorage.setItem("subaccountIdToFilterBatch", "");
      handleSearchClear();
    };

    const handleInputChange = async (event: any) => {
      const { id, value } = event.target;
      setFilters({ ...filters, [id]: value });

      if (id === "date_type") {
        if (value === "p") {
          setMaxDate(false);
        } else {
          setMaxDate(new Date());
        }
      }
    };

    if (!showFilters) return null;

    return (
      <>
        {/* Subaccount Selection Modal - outside the Box */}
        {isSubaccountModalOpen && (
          <BatchSearchSubaccountListModal
            isOpen={isSubaccountModalOpen}
            onClose={() => setIsSubaccountModalOpen(false)}
            onSelect={handleSubaccountSelect}
          />
        )}
        {/* Provider Selection Modal */}
        {isProviderModalOpen && (
          <ProviderListModal
            isOpen={isProviderModalOpen}
            onClose={() => setIsProviderModalOpen(false)}
            onSelect={handleProviderSelect}
          />
        )}
        <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
          <div className={`grid grid-cols-1 gap-4 ${userRole === "merchant" ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Date Type
              </label>
              <Select
                isDisabled={isLoading}
                id="date_type"
                value={filters.date_type}
                onChange={handleInputChange}
                className="custom-select  relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="c">Created Date</option>
                <option value="p">Process Date</option>
              </Select>
            </div>
            <div className="search-picker-custom tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Date
              </label>
              <RangeDatepicker
                disabled={isLoading}
                selectedDates={selectedDates}
                onDateChange={setSelectedDates}
                propsConfigs={dateRangePropsConfigs}
                configs={{
                  dateFormat: "MMM d, yyyy",
                }}
                maxDate={maxDate}
              />
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Asset
              </label>
              <Select
                isDisabled={isLoading || !assetList}
                id="asset"
                value={filters.asset}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">{assetList ? "All" : "Retrieving..."}</option>
                {assetList &&
                  assetList.filter((item: any) => !item.crypto).length > 0 &&
                  assetList.filter((item: any) => !item.crypto) && (
                    <optgroup label="Fiat">
                      {assetList &&
                        assetList
                          .filter((item: any) => !item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
                {assetList &&
                  assetList.filter((item: any) => item.crypto).length > 0 &&
                  assetList.filter((item: any) => item.crypto) && (
                    <optgroup label="Crypto">
                      {assetList &&
                        assetList
                          .filter((item: any) => item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
              </Select>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Status
              </label>
              <Select
                isDisabled={isLoading}
                id="status"
                value={filters.status}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">All</option>
                <option value="-2">Open</option>
                <option value="-1">Closed</option>
                <option value="0">Queued</option>
                <option value="1">In Progress</option>
                <option value="2">Settled</option>
                <option value="3">Settled with Error</option>
                <option value="7">Settling</option>
                <option value="6">Cancelling</option>
                <option value="4">Cancelled</option>
                <option value="5">Failed</option>
              </Select>
            </div>

            {userRole === "superadmin" && (
              <>
                <div className="tracking-tight text-gray-900">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                    Provider
                  </label>
                  <Tooltip
                    title={selectedProvider ? selectedProvider.name : "All"}
                    arrow
                    placement="top"
                  >
                    <div
                      onClick={() => !isFilterLoading && !isLoading && setIsProviderModalOpen(true)}
                      className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                        isFilterLoading || isLoading
                          ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                          : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                      }`}
                    >
                      <span className="truncate">
                        {selectedProvider ? selectedProvider.name : "All"}
                      </span>
                      {selectedProvider && !isFilterLoading && !isLoading && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleClearProvider(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </Tooltip>
                </div>
                <div className="tracking-tight text-gray-900">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                    Method
                  </label>
                  <Select
                    isDisabled={isLoading || !selectedProvider}
                    id="payout_method"
                    value={filters.payout_method}
                    onChange={handleInputChange}
                    className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                  >
                    <option value="">All</option>
                    {methodList.map((method: any) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="tracking-tight text-gray-900">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                    Subaccount
                  </label>
                  <Tooltip
                    title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                    arrow
                    placement="top"
                  >
                    <div
                      onClick={() => !isFilterLoading && !isLoading && setIsSubaccountModalOpen(true)}
                      className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${isFilterLoading || isLoading
                          ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                          : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                        }`}
                    >
                      <span className="truncate">
                        {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                      </span>
                      {selectedSubaccount && !isFilterLoading && !isLoading && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </Tooltip>
                </div>
                <div className="flex items-end justify-end gap-2 pt-2">
                    <Button
                      isDisabled={isLoading || isFilterLoading}
                      onClick={handleClear}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
                    >
                      Clear
                    </Button>

                    <Button
                      isDisabled={isLoading || isFilterLoading}
                      onClick={handleSearchSubmit}
                      className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="relative z-10 tracking-tight">
                        Search Batches
                      </span>
                      <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                    </Button>
                </div>
              </>
            )}
            {userRole === "merchant" && (
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Subaccount
                </label>
                <Tooltip
                  title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && !isLoading && setIsSubaccountModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${isFilterLoading || isLoading
                        ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                        : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                      }`}
                  >
                    <span className="truncate">
                      {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                    </span>
                    {selectedSubaccount && !isFilterLoading && !isLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}
          </div>
          {userRole !== "superadmin" && (
            <div className="mt-4 flex flex-1 items-end justify-end gap-2">
              <Button
                isDisabled={isLoading || isFilterLoading}
                onClick={handleClear}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                Clear
              </Button>
              <Button
                isDisabled={isLoading || isFilterLoading}
                onClick={handleSearchSubmit}
                className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="relative z-10 tracking-tight">Search Batches</span>
                <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
              </Button>
            </div>
          )}
        </Box>
      </>
    );
  };

export default BatchSearch;
